import React from 'react';
import PropTypes from 'prop-types';

import { StyledTextFieldContainer } from '../../../../../components/Inputs';
import PopupColorPicker, { HexColorInput } from '../../../../../components/PopupColorPicker';
import InputLabel from '../../../../../components/v2/InputLabel';
import { ColorModes } from '../../../../../Model/Coach';
import { themeColorMapping } from '../../../../../styles/colors';
import { isPrimaryColorEnabled } from '../../../../../utils/postHog';
import AutoComplete from '../../../../components/AutoComplete';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';
import { ThemeSectionContainer } from '../../styles';
import texts from './texts.json';

// An empty color mode leaves the mode to the coach's settings.
const COACH_COLOR_MODE = '';

const colorModeLabel = (colorMode) => texts.colorModes[colorMode] || texts.coachColorMode;

/**
 * The mobile app's look: a preset theme, then, for the admins in the primary color flag, a primary color that
 * overrides the preset's and the color mode. Picking a preset also picks its color. The coach's settings and a
 * product's override of them both edit it. A product's can follow the coach's (`followsCoach`): its color mode has a
 * coach's option, and clearing its theme clears its color, since the color input cannot be left empty.
 */
const AppThemeFields = ({
  themeOptions,
  themeId,
  onThemeChange,
  primaryColor,
  onPrimaryColorChange,
  colorMode,
  onColorModeChange,
  followsCoach,
}) => {
  const isCustomizable = isPrimaryColorEnabled();

  const handleThemeChange = (theme) => {
    onThemeChange(theme);
    const themePrimaryColor = themeColorMapping[theme?.label];
    if (themePrimaryColor) {
      onPrimaryColorChange(themePrimaryColor);
    } else if (!theme && followsCoach) {
      onPrimaryColorChange('');
    }
  };

  const colorModes = [
    ...(followsCoach ? [COACH_COLOR_MODE] : []),
    ColorModes.DARK,
    ColorModes.LIGHT,
  ];

  return (
    <>
      <ThemeSectionContainer>
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.appTheme}</InputLabel>
          <AutoComplete
            initialValue={themeId}
            options={themeOptions}
            onChange={handleThemeChange}
          />
        </StyledTextFieldContainer>
        {isCustomizable && (
          <>
            <StyledTextFieldContainer $withMargin>
              <InputLabel>{texts.appPrimaryColor}</InputLabel>
              <HexColorInput
                placeholder={texts.colorPlaceholder}
                color={primaryColor}
                onChange={onPrimaryColorChange}
              />
            </StyledTextFieldContainer>
            <PopupColorPicker color={primaryColor} onChange={onPrimaryColorChange} />
          </>
        )}
      </ThemeSectionContainer>
      {isCustomizable && (
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.appColorMode}</InputLabel>
          <RadioButtonGroup
            options={colorModes.map((mode) => ({ label: colorModeLabel(mode), value: mode }))}
            selectedOption={colorMode}
            onOptionChange={onColorModeChange}
            isRow
          />
        </StyledTextFieldContainer>
      )}
    </>
  );
};

AppThemeFields.propTypes = {
  themeOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })).isRequired,
  themeId: PropTypes.string,
  onThemeChange: PropTypes.func.isRequired,
  primaryColor: PropTypes.string,
  onPrimaryColorChange: PropTypes.func.isRequired,
  colorMode: PropTypes.string,
  onColorModeChange: PropTypes.func.isRequired,
  followsCoach: PropTypes.bool,
};

AppThemeFields.defaultProps = {
  themeId: null,
  primaryColor: '',
  colorMode: COACH_COLOR_MODE,
  followsCoach: false,
};

export { colorModeLabel };
export default AppThemeFields;
